import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getJson } from "./getJson.js";
import { queryKeys } from "./query-keys.js";

export interface StrategySetting {
  key: string;
  label: string;
  description: string;
  family: string;
  signalTypes: string[];
  enabled: boolean;
}

export interface StrategySettingsResponse {
  strategies: StrategySetting[];
}

const EMPTY: ReadonlySet<string> = new Set();

/**
 * Its own module rather than a hook in `queries.ts`, which is 437 lines and
 * past the 400-line mark where CLAUDE.md rule 7 stops anyone editing a file
 * without an approved split plan. `getJson.ts:1-8` made the same call.
 *
 * The list itself comes from the API rather than a local constant: web
 * depends on `@savi/api`, not `@savi/core`, so it cannot import the
 * catalogue — and rendering whatever the server sends means there is no sixth
 * copy of the strategy list to keep in sync.
 */
export function useStrategySettings() {
  return useQuery({
    queryKey: queryKeys.strategySettings(),
    queryFn: () => getJson<StrategySettingsResponse>("/api/settings/strategies"),
    staleTime: 5 * 60_000,
  });
}

/** The types a switched-off strategy has withdrawn, for the filter controls. */
export function useHiddenSignalTypes(): ReadonlySet<string> {
  const { data } = useStrategySettings();
  if (!data) return EMPTY;
  return new Set(
    data.strategies.filter((entry) => !entry.enabled).flatMap((entry) => entry.signalTypes),
  );
}

/**
 * Optimistic, because a switch that waits for a round trip reads as broken.
 *
 * Rollback is the whole previous response rather than the one entry, so a
 * failed write cannot leave the page holding a state the server never
 * acknowledged.
 */
export function useSetStrategyEnabled() {
  const client = useQueryClient();
  const key = queryKeys.strategySettings();

  return useMutation({
    mutationFn: async ({ strategy, enabled }: { strategy: string; enabled: boolean }) => {
      const response = await fetch(`/api/settings/strategies/${encodeURIComponent(strategy)}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      if (!response.ok) {
        throw new Error(`setting ${strategy} returned ${response.status}`);
      }
    },
    onMutate: async ({ strategy, enabled }) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<StrategySettingsResponse>(key);
      if (previous) {
        client.setQueryData<StrategySettingsResponse>(key, {
          strategies: previous.strategies.map((entry) =>
            entry.key === strategy ? { ...entry, enabled } : entry,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
    },
    onSettled: () => {
      client.invalidateQueries({ queryKey: key });
      // Switching a strategy off hides the rows it already wrote, so every
      // cache holding signals is now wrong. Marked stale rather than
      // refetched, the way `acknowledge.ts` does it: the next mount with a
      // reader in front of it is when a divergence would matter.
      for (const prefix of [["signals"], ["signal"], ["symbol"], ["intraday"]]) {
        client.invalidateQueries({ queryKey: prefix, refetchType: "none" });
      }
    },
  });
}
